import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import accountApi from '~/apis/account';
import { CloseIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { ROLE_AGENT } from '~/utils/constants/account';
import { cast, handleApiError, notify } from '~/utils/helper';
import { IAccount, IGetAllParams } from '~/utils/interface/account';
import { useAppDispatch } from '~/redux/hooks';
import { commonActions } from '~/thunks/common/commonSlice';
import notificationApi from '~/apis/notification';
import styles from '../../Distributor.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

type SendNotificationDialogProps = {
  open: boolean;
  data?: IAccount;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IGetAllParams = {
  order_by: {
    field: '',
    type: '',
  },
  page_index: 1,
  page_size: 10,
};

const SendNotificationDialog = (props: SendNotificationDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/distributor', 'admin/common']);

  const [totalItems, setTotalItems] = useState<number>(0);
  const [accountList, setAccountList] = useState<IAccount[]>([]);

  const form = useFormik<IGetAllParams>({
    initialValues,
    onSubmit: async () => {
      handleGetAgents();
    },
  });

  useEffect(() => {
    handleGetAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.page_index, form.values.page_size]);

  const getAgents = async () => {
    try {
      return (
        await accountApi.findByRoleList(
          { role: ROLE_AGENT, username: props.data?.username },
          {
            ...form.values,
          }
        )
      ).data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, data: [] };
    }
  };

  const handleGetAgents = async () => {
    const response = await getAgents();
    setTotalItems(response.total);
    setAccountList(response.data);
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleConfirmSendInfoToAgent = async () => {
    dispatch(commonActions.setIsLoading(true));
    try {
      await notificationApi.transferDistributor({ username: cast(props.data?.username) });
      notify('success', t('sendNotifySuccess', { ns: 'admin/common' }));
      handleSetOpen(false, true);
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch(commonActions.setIsLoading(false));
    }
  };

  return (
    <Box>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='lg'>
        <DialogTitle className='bg-[#ED1002]'>
          {t('sendNotificationDialog.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={cx('transfer-container')}>
          <Typography className='p-3 mb-4 rounded-[4px] bg-[#FF9500] border border-[#005F73] text-white font-bold '>
            {t('sendNotificationDialog.description', { name: props.data?.name })}
          </Typography>
          <Typography className='text-[#005F73] mb-2 font-bold'>{t('sendNotificationDialog.info')}</Typography>
          <Box className='flex flex-row gap-12 mb-5'>
            <Box>
              <Box className='flex flex-row gap-3.5 mb-2'>
                <Box className='font-bold'>{t('editDialog.name')}:</Box>
                <Box>{props.data?.name}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-2'>
                <Box className='font-bold'>{t('editDialog.address')}:</Box>
                <Box>{props.data?.address}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-2'>
                <Box className='font-bold'>{t('editDialog.representative')}:</Box>
                <Box>{props.data?.representative}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-2'>
                <Box className='font-bold'>{t('editDialog.phone')}:</Box>
                <Box>{props.data?.phone}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-2'>
                <Box className='font-bold'>{t('editDialog.email')}:</Box>
                <Box>{props.data?.email}</Box>
              </Box>
            </Box>
            <Box>
              <Box className='flex flex-row gap-3.5 mb-2'>
                <Box className='font-bold'>{t('editDialog.username')}:</Box>
                <Box>{props.data?.username}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-2'>
                <Box className='font-bold'>{t('editDialog.contract_id')}:</Box>
                <Box>{props.data?.contract_id}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5'>
                <Box className='font-bold'>{t('editDialog.created_at')}:</Box>
                <Box>{props.data?.created_at ? dayjs(props.data.created_at).format('DD/MM/YYYY') : ''}</Box>
              </Box>
            </Box>
          </Box>
          <Box className='border border-[#005F73] text-[#005F73] rounded-[10px] p-2.5 mb-5'>
            <Box className='flex flex-wrap justify-between items-center border-b border-[#D9D9D9]'>
              <Typography className='text-[#005F73] mb-2 font-bold'>{t('sendNotificationDialog.agentList')}</Typography>
              <Box className='rounded-full text-white h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                {totalItems}
              </Box>
            </Box>
            <Box className='flex flex-wrap border-b border-[#D9D9D9] py-1.5'>
              {accountList.map((account: IAccount, index: number) => {
                return (
                  <Typography key={index} className='w-1/5 py-1.5'>
                    {account.username}
                  </Typography>
                );
              })}
            </Box>
            <Box className={cx('flex items-center justify-end gap-10 mt-1', 'pagination-box')}>
              <Typography>
                <span className='text-black'>{t('sum')}</span>:
                <span className='font-bold text-[#005f73] mx-7'>{totalItems}</span>
                <span className='text-black'>{t('sendNotificationDialog.record')}</span>
              </Typography>
              <Box className='w-[150px]'>
                <BaseSelect
                  options={[
                    { id: 10, name: '10' },
                    { id: 20, name: '20' },
                    { id: 50, name: '50' },
                    { id: 100, name: '100' },
                    { id: 250, name: '250' },
                    { id: 500, name: '500' },
                  ]}
                  value={form.values.page_size}
                  onChange={(e) => {
                    form.setFieldValue('page_size', e.target.value);
                    form.setFieldValue('page_index', 1);
                  }}
                  renderSelected={(option: any) => (
                    <Typography className='px-2'>
                      {option.name}/{t('page').toLowerCase()}
                    </Typography>
                  )}
                />
              </Box>
              <Pagination
                count={Math.max(1, Math.ceil(totalItems / (form.values.page_size ?? 1)))}
                variant='outlined'
                page={form.values.page_index ?? 1}
                onChange={(e, value: number) => {
                  form.setFieldValue('page_index', value);
                }}
              />
              <Box className='flex items-center gap-3'>
                <Typography>{t('page')}</Typography>
                <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>
                  {form.values.page_index}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' className='min-w-[125px]' onClick={() => handleSetOpen(false)}>
            {t('cancel', { ns: 'admin/common' })}
          </Button>
          <Button
            disabled={!accountList?.length}
            variant='contained'
            color='secondary'
            className='min-w-[125px] flex ml-auto'
            onClick={handleConfirmSendInfoToAgent}
          >
            {t('sendNotificationDialog.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(SendNotificationDialog);
